import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Flame, MapPin } from "lucide-react";
import { useCart } from "../context/CartContext";
import api from "../lib/api";

const TIME_SLOTS = ["11:00", "11:30", "12:00", "12:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00"];

const Checkout = () => {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    pickup_date: "",
    pickup_time: "12:00",
    notes: "",
  });

  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Your cart is empty.");
      return;
    }
    if (!form.customer_name || !form.customer_email || !form.customer_phone || !form.pickup_date) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        ...form,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/checkout/session", payload);
      // Persist session id so success page can poll
      localStorage.setItem("be_last_session_id", data.session_id);
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed. Try again.");
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="checkout-empty">
        <h1 className="font-display text-5xl text-cream">EMPTY CART</h1>
        <p className="text-[#A19D94] mt-4">Add something delicious from the pit first.</p>
        <Link to="/shop" className="inline-block mt-8 px-8 h-14 bg-brand text-ink-900 font-display text-2xl tracking-widest leading-[3.5rem]">BROWSE SHOP</Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
      <div className="border-b border-ink-600 pb-8 mb-10">
        <div className="text-brand font-display text-sm tracking-[0.4em] mb-2">/ CHECKOUT</div>
        <h1 className="font-display text-5xl sm:text-6xl text-cream leading-none">LOCK IN YOUR PICKUP</h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-10">
        <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-8">
          <section>
            <h2 className="font-display text-2xl text-brand tracking-widest mb-4">CONTACT</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full Name" required>
                <input
                  required
                  data-testid="checkout-name"
                  value={form.customer_name}
                  onChange={(e) => update("customer_name", e.target.value)}
                  className="input-dark"
                />
              </Field>
              <Field label="Phone" required>
                <input
                  required
                  data-testid="checkout-phone"
                  value={form.customer_phone}
                  onChange={(e) => update("customer_phone", e.target.value)}
                  className="input-dark"
                />
              </Field>
              <Field label="Email" required className="sm:col-span-2">
                <input
                  required
                  type="email"
                  data-testid="checkout-email"
                  value={form.customer_email}
                  onChange={(e) => update("customer_email", e.target.value)}
                  className="input-dark"
                />
              </Field>
            </div>
          </section>

          <section>
            <h2 className="font-display text-2xl text-brand tracking-widest mb-4">PICKUP DETAILS</h2>
            <div className="border border-ink-600 bg-ink-800 p-4 mb-5 flex items-start gap-3">
              <MapPin className="text-brand mt-1 flex-shrink-0" size={18} />
              <div>
                <div className="font-display text-xl text-cream tracking-wide">GOSFORD SHOWGROUNDS</div>
                <div className="text-sm text-[#A19D94]">Showground Road, Gosford NSW. We'll text the exact stall when you're 10 mins out.</div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Pickup Date" required>
                <input
                  required
                  type="date"
                  min={tomorrow}
                  data-testid="checkout-pickup-date"
                  value={form.pickup_date}
                  onChange={(e) => update("pickup_date", e.target.value)}
                  className="input-dark"
                />
              </Field>
              <Field label="Pickup Time" required>
                <select
                  required
                  data-testid="checkout-pickup-time"
                  value={form.pickup_time}
                  onChange={(e) => update("pickup_time", e.target.value)}
                  className="input-dark"
                >
                  {TIME_SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Notes (optional)" className="sm:col-span-2">
                <textarea
                  rows={3}
                  data-testid="checkout-notes"
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  className="input-dark"
                  placeholder="Allergies, extras, etc."
                />
              </Field>
            </div>
          </section>
        </form>

        <aside className="bg-ink-800 border border-ink-600 p-6 h-fit sticky top-28">
          <h2 className="font-display text-2xl text-cream tracking-widest mb-4 flex items-center gap-2">
            <Flame size={18} className="text-brand" /> YOUR ORDER
          </h2>
          <ul className="space-y-3 max-h-64 overflow-y-auto">
            {items.map((i) => (
              <li key={i.product_id} className="flex justify-between gap-3 text-sm">
                <div className="flex-1">
                  <div className="text-cream font-display text-base leading-none">{i.name}</div>
                  <div className="text-[#A19D94] text-xs mt-1">{i.quantity} × ${i.price.toFixed(2)}</div>
                </div>
                <div className="text-cream font-display">${(i.price * i.quantity).toFixed(2)}</div>
              </li>
            ))}
          </ul>
          <div className="border-t border-ink-600 mt-5 pt-5 space-y-2">
            <div className="flex justify-between text-[#A19D94] text-sm">
              <span>Subtotal</span>
              <span>${subtotal.toFixed(2)} AUD</span>
            </div>
            <div className="flex justify-between text-[#A19D94] text-sm">
              <span>Pickup</span>
              <span>FREE</span>
            </div>
            <div className="flex justify-between items-end pt-3 border-t border-ink-600 mt-2">
              <span className="font-display text-xl text-cream tracking-widest">TOTAL</span>
              <span className="font-display text-3xl text-brand">${subtotal.toFixed(2)}</span>
            </div>
          </div>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="checkout-pay-btn"
            className="w-full mt-6 h-14 bg-brand hover:bg-brand-hover text-ink-900 font-display text-2xl tracking-widest disabled:opacity-50 transition-all teal-glow-hover"
          >
            {submitting ? "LOADING..." : "PAY WITH STRIPE"}
          </button>
          <p className="text-xs text-[#A19D94] mt-3 text-center">Test card: 4242 4242 4242 4242</p>
        </aside>
      </div>

      <style>{`
        .input-dark {
          width: 100%;
          background: #0A0A0C;
          border: 1px solid #26292F;
          color: #F5F2EA;
          padding: 0.75rem 1rem;
          font-family: 'Outfit', sans-serif;
          outline: none;
          transition: border-color 0.2s;
        }
        .input-dark:focus { border-color: #1BA4C4; box-shadow: 0 0 0 1px #1BA4C4; }
      `}</style>
    </div>
  );
};

const Field = ({ label, children, required, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="block text-xs tracking-[0.25em] text-[#A19D94] mb-2 uppercase">
      {label} {required && <span className="text-brand">*</span>}
    </span>
    {children}
  </label>
);

export default Checkout;
